/**
 * Client-side YAML assembler (Spec 4 — generation only, NO backend/upload).
 *
 * Takes the wizard's fully-populated `OnboardingConfig` and renders it into the
 * final Bluey tool-config YAML string (the shape consumed by
 * DSDIBlueyService's UniversalStoredProcedureConfig — see the reference config
 * `prophecy_v3_universal_config.yaml`).
 *
 * Metric rows come from the shared `catalogRows` builder (NOT assembled here), so
 * the emitted `derived_metrics`, the `metrics` param enum, and the rows written to
 * the Redshift catalog tables are the same list by construction — the stored
 * procedure resolves requested metrics against those tables, so any disagreement
 * is a runtime failure.
 *
 * Two responsibilities:
 *   1. Build a plain JS object whose keys are inserted in the exact top-level
 *      order the reference YAML uses, then `yaml.dump` it (sortKeys:false keeps
 *      that insertion order; lineWidth:-1 keeps long SQL/description lines and
 *      emits multi-line strings as block scalars).
 *   2. Reverse the display-string coercions the Parameters step applied
 *      (`coerceDefault` in llmParameterAdvisor.ts JSON-stringifies arrays/objects
 *      and stores every default as a string) so `default` round-trips to a real
 *      typed value in the YAML.
 *
 * It also surfaces non-blocking `warnings[]` for empty/under-specified config so
 * the UI can nudge the user without gating generation.
 */

import * as yaml from 'js-yaml';
import {
  buildCatalogRows,
  CATALOG_SCHEMA_PATTERN,
  DerivedCatalogRow,
  resolveCatalogTables,
} from './catalogRows';
import { OnboardingConfig, ParameterDraft } from './types';

const OUTPUT_SCHEMA = 'bluey_output';
const DEFAULT_STORED_PROCEDURE = 'sp_bluey_common_report';

// The input-schema parameter whose enum lists the selectable metric names. Its
// enum must cover every derived_metric_key (including synthesized passthroughs)
// or Bluey cannot select those metrics. Reconciled at assembly time.
const METRICS_PARAM_NAME = 'metrics';

export interface YamlAssemblyResult {
  yaml: string;
  warnings: string[];
}

// Top-level keys that begin a major section. We insert a blank line before each
// (when not already blank-separated) so the emitted file visually groups like the
// reference config (prophecy_v3), which separates database/base_metrics/etc. with
// blank lines. The catalog trio (base_metrics_catalog/derived_metrics_catalog/
// output_schema) intentionally stays grouped with source_table_name. Purely
// cosmetic — YAML ignores blank lines, so `yaml.load` round-trips unchanged.
const SPACED_SECTION_KEYS = [
  'database',
  'stored_procedure',
  'source_table_name',
  'base_metrics',
  'derived_metrics',
  'metadata',
  'input_schema',
  'required',
];

/**
 * Inserts a blank line before each top-level section key for readability, matching
 * the reference config's visual grouping. Only matches keys at column 0 (the key
 * name sits immediately after the newline with no indentation), so nested keys of
 * the same name — e.g. a `database:` under a param — are never touched. Matching
 * one-or-more leading newlines (`\n+`) and collapsing them to exactly two keeps it
 * idempotent (an already-spaced key stays single-blank-line separated) without an
 * ES2018 lookbehind, which the site's browserslist targets predate.
 */
function addSectionSpacing(yamlStr: string): string {
  let result = yamlStr;
  for (const key of SPACED_SECTION_KEYS) {
    result = result.replace(new RegExp(`\\n+(${key}:)`, 'g'), '\n\n$1');
  }
  return result;
}

/**
 * Rewrites every block-style `enum:` sequence into a single-line flow sequence
 * with every string value double-quoted (`enum: ["filter", "YoY", "MoM"]`),
 * matching the reference configs — prophecy_v3, axis_v4, and cerberus all quote
 * every enum value, none emit bare tokens. js-yaml has no per-key flow-style
 * control — `flowLevel` is keyed by nesting depth, so it would also collapse
 * `examples`, `items`, etc. — so we post-process just the `enum:` blocks here.
 *
 * Item tokens are NOT reused verbatim: block-context and flow-context plain scalars
 * have different quoting rules (a comma or bracket that js-yaml leaves unquoted in a
 * block item — `- a,b` — would split or break inside `[…]`). So we `yaml.load` each
 * rendered token back to its value and re-`dump` the whole array in flow style with
 * `forceQuotes`. That guarantees `yaml.load` round-trips to the identical array
 * (e.g. `"a,b"` stays one element) AND matches the reference always-quoted style;
 * non-strings (numbers, an empty list) stay unquoted, as they do in the references.
 *
 * Only `enum:` with a block body is touched; an inline `enum: [x]` / `enum: []` has
 * no trailing `- ` items, so `items` is empty and the header is left verbatim. That
 * empty-items guard also protects a parameter literally NAMED `enum` (its spec
 * sub-keys aren't `- ` items, so the block is left intact).
 */
function collapseEnums(yamlStr: string): string {
  const lines = yamlStr.split('\n');
  const out: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Match `<indent>enum:` with nothing (but optional trailing space) after the
    // colon — i.e. a block sequence follows on subsequent lines. `enum: [x]` or
    // `enum: value` already has an inline body and is skipped.
    const header = line.match(/^(\s*)enum:\s*$/);
    if (!header) {
      out.push(line);
      continue;
    }

    const enumIndent = header[1];
    // A block-sequence item is indented deeper than the key and starts with `- `.
    const itemPattern = new RegExp(`^${enumIndent}\\s+- (.*)$`);
    const items: string[] = [];
    let j = i + 1;
    for (; j < lines.length; j++) {
      const m = lines[j].match(itemPattern);
      if (!m) break;
      items.push(m[1]);
    }

    if (items.length === 0) {
      // Bare `enum:` header with no block items — e.g. a parameter NAMED `enum`,
      // whose nested spec keys aren't `- ` items. Leave the line untouched so the
      // block below it is preserved intact.
      out.push(line);
      continue;
    }

    // Reparse each rendered scalar token, then re-serialize the array in flow style
    // so js-yaml re-quotes for flow context (safe for commas, brackets, etc.).
    // forceQuotes double-quotes every string value to match the reference configs
    // (prophecy_v3/axis_v4/cerberus all emit `["filter", "YoY", "MoM"]`, never bare);
    // non-strings (numbers, the empty list) stay unquoted, as they do there.
    const values = items.map((token) => yaml.load(token));
    const flow = yaml
      .dump(values, { flowLevel: 0, lineWidth: -1, noRefs: true, quotingType: '"', forceQuotes: true })
      .trimEnd();
    out.push(`${enumIndent}enum: ${flow}`);
    i = j - 1; // Skip the consumed item lines.
  }

  return out.join('\n');
}

/**
 * Rewrites block-style `examples:` sequences into a single fully-bracketed
 * inline flow line, matching the reference configs that use this style (e.g.
 * axis_config): `examples: [["country"], ["day", "channel"]]`. `collapseEnums`
 * can't be reused because an `examples` item is often itself an array (array-typed
 * params), so js-yaml block-dumps each example into sprawling nested bullets —
 * `- - "country"`, or for a date-range param `- - - "2025-01-01"`. We `yaml.load`
 * the whole block once and re-`dump` the entire list in flow style, so the nesting
 * stays bracketed on one line instead of exploding:
 *
 *     examples:              →   examples: [["country"], ["day", "channel"]]
 *       - - country
 *       - - day
 *         - channel
 *
 *     examples:              →   examples: [[["2025-01-01", "2025-03-31"]], [["2024-01-01", "2024-12-31"]]]
 *       - - - "2025-01-01"
 *           - "2025-03-31"
 *       ...
 *
 * A flat SCALAR examples list (e.g. `method`'s ["filter","YoY","MoM"]) renders the
 * same way — `examples: ["filter", "YoY", "MoM"]`. Every string value is
 * double-quoted (`forceQuotes`) to match the reference configs, which quote every
 * enum/examples string value; non-strings (numbers, the empty list `[]`) stay
 * unquoted, as they do there. Same treatment as `collapseEnums`.
 *
 * Unlike enum items, an examples item can span MULTIPLE lines (a nested block),
 * so we capture the whole block body by indentation, `yaml.load` it once, and
 * re-emit — rather than matching line-by-line.
 */
function collapseExamples(yamlStr: string): string {
  const lines = yamlStr.split('\n');
  const out: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const header = line.match(/^(\s*)examples:\s*$/);
    if (!header) {
      out.push(line);
      continue;
    }

    const keyIndent = header[1];
    // The block body is every following line indented deeper than the key. This
    // captures both the `- ` item lines and any nested continuation lines of a
    // multi-line (array-valued) item.
    const bodyIndentRe = new RegExp(`^${keyIndent}\\s`);
    let j = i + 1;
    const bodyLines: string[] = [];
    for (; j < lines.length; j++) {
      if (lines[j].trim() === '') break; // blank line ends the block
      if (!bodyIndentRe.test(lines[j])) break; // dedent ends the block
      bodyLines.push(lines[j]);
    }

    // No block body (inline `examples: [...]` or a param literally named
    // `examples` whose sub-keys aren't `- ` items) — leave the header verbatim.
    if (bodyLines.length === 0 || !bodyLines[0].trimStart().startsWith('- ')) {
      out.push(line);
      continue;
    }

    // Reparse the whole block to the array of example values.
    const examples = yaml.load(bodyLines.join('\n'));
    if (!Array.isArray(examples)) {
      out.push(line);
      continue;
    }

    // Render the WHOLE examples list inline on one line, fully bracketed —
    // `examples: [["country"], ["day", "channel"]]`, and a flat scalar list as
    // `examples: ["filter", "YoY", "MoM"]`. Nested arrays (array-typed params,
    // date-range tuples) stay bracketed rather than exploding into block bullets:
    // `examples: [[["2025-01-01", "2025-03-31"]], [["2024-01-01", "2024-12-31"]]]`.
    // flowLevel 0 forces flow style at every depth; lineWidth -1 keeps it on one
    // line. forceQuotes double-quotes every string to match the reference configs
    // (never bare); non-strings (numbers, the empty list `[]`) stay unquoted, as
    // they do there.
    const flow = yaml
      .dump(examples, { flowLevel: 0, lineWidth: -1, noRefs: true, quotingType: '"', forceQuotes: true })
      .trimEnd();
    out.push(`${keyIndent}examples: ${flow}`);
    i = j - 1; // Skip the consumed body lines.
  }

  return out.join('\n');
}

/**
 * Reverses `coerceDefault` (llmParameterAdvisor.ts): the wizard stores every
 * parameter default as a display STRING (arrays/objects JSON-stringified). Here
 * we parse it back to the typed value the YAML should carry, keyed off the
 * declared param type. `null` default → caller omits the key entirely.
 */
function parseDefaultValue(param: ParameterDraft): unknown {
  const raw = param.defaultValue;
  if (raw === null || raw === undefined) return undefined;

  switch (param.type) {
    case 'number': {
      // Number('') and Number('   ') are 0 (not NaN), so an empty/whitespace-only
      // default would silently coerce to 0 — short-circuit it to the raw string first.
      if (raw.trim() === '') return raw;
      const n = Number(raw);
      // A non-numeric string (e.g. "abc") becomes NaN — keep the original string
      // rather than emit an invalid `default: .nan`.
      return Number.isNaN(n) ? raw : n;
    }
    case 'boolean':
      return raw === 'true';
    case 'array':
      try {
        return JSON.parse(raw);
      } catch {
        // Not valid JSON (hand-edited) — fall back to the raw string.
        return raw;
      }
    default:
      return raw;
  }
}

/** Builds one `input_schema` entry, spreading opaque `raw` keys and reversing default coercion. */
function buildParamSpec(param: ParameterDraft): Record<string, unknown> {
  const spec: Record<string, unknown> = {
    // Spread the opaque LLM fields the table never rendered (items, examples, …)
    // FIRST so they survive the round-trip, then let the authoritative
    // type/description/required override any stray colliding key. enum/default
    // are reconciled explicitly below (they are set conditionally, so a spread
    // copy must be deleted when the wizard state has none).
    ...(param.raw ?? {}),
    type: param.type,
    description: param.description,
    required: param.required,
  };

  // enum/default are set CONDITIONALLY, so (unlike type/description/required
  // above) a stale copy spread from `raw` would survive when the wizard state
  // has none. Delete in the empty case so the authoritative wizard state always
  // wins — even if a future re-import path lets `raw` carry these keys.
  if (param.enumValues && param.enumValues.length > 0) {
    spec.enum = param.enumValues;
  } else {
    delete spec.enum;
  }

  const parsedDefault = parseDefaultValue(param);
  if (parsedDefault !== undefined) {
    spec.default = parsedDefault;
  } else {
    delete spec.default;
  }

  return spec;
}

/**
 * Assembles the wizard config into the final YAML tool-config string plus a set
 * of non-blocking warnings. Pure/synchronous — no network, no upload.
 */
export function assembleYaml(config: OnboardingConfig): YamlAssemblyResult {
  const warnings: string[] = [];

  // ── Warnings (non-blocking) ────────────────────────────────────────────
  if (!config.toolDescription || config.toolDescription.trim() === '') {
    warnings.push('Description is empty — the tool will have no LLM guidance.');
  }
  if (config.baseMetrics.length === 0) {
    warnings.push('No base metrics defined.');
  }
  if (config.parameters.length === 0) {
    warnings.push('No parameters defined — the tool will accept no input.');
  } else if (!config.parameters.some((p) => p.required)) {
    warnings.push('No parameters are marked required.');
  }
  const { test, beta, live } = config.database.secretArns;
  if (!test || !beta || !live) {
    warnings.push('One or more secret ARNs are missing.');
  }

  // ── Metric rows ────────────────────────────────────────────────────────
  // Built by the SHARED catalog-row builder, which also feeds the Step-6 catalog
  // tables, the downloadable CSVs, and the write-catalogs API call. Sharing it is
  // what guarantees the YAML's derived_metrics, the `metrics` enum below, and the
  // rows actually written to Redshift can never disagree — the SP resolves every
  // requested metric against those TABLES, so a mismatch is a runtime failure.
  const catalogRows = buildCatalogRows(config);
  const catalogTables = resolveCatalogTables(config);
  const synthesizedCount = catalogRows.synthesizedCount;

  // base_metrics: drop the `filter_condition` key entirely when there is none
  // (the shared builder normalizes blank -> null; the YAML omits it).
  const baseMetrics = catalogRows.base.map((m) => {
    const entry: Record<string, unknown> = {
      metric_key: m.metric_key,
      expr_sql: m.expr_sql,
      agg: m.agg,
    };
    if (m.filter_condition !== null) {
      entry.filter_condition = m.filter_condition;
    }
    return entry;
  });

  // User-defined derived metrics first (original order), then the auto-added
  // ROUND(<base>,2) passthroughs. Deliberately NOT warned about here: adding them
  // is automatic and desirable, so a yellow warning misreads as a problem. The
  // catalog section states the count neutrally, next to the rows themselves.
  const derivedMetrics: DerivedCatalogRow[] = catalogRows.derived;

  // An invalid catalog schema would emit an unusable identifier (e.g.
  // `9bad-name.meta_x_metrics_catalog`). The final step is permissive by design
  // — it never gates Finish — so surface it as a warning rather than blocking.
  if (!CATALOG_SCHEMA_PATTERN.test(catalogTables.schema)) {
    warnings.push(
      `Catalog schema "${catalogTables.schema}" is not a valid Redshift identifier — ` +
        'the emitted base_metrics_catalog / derived_metrics_catalog will be unusable.',
    );
  }

  // ── input_schema (keyed by param name) + required list ─────────────────
  // The `metrics` param's enum is what actually gates which metrics Bluey can
  // select. It is authored earlier (LLM parameter inference) from a snapshot of
  // the EXPLICIT derived metrics only, so it misses the passthroughs synthesized
  // above (and can also lag hand-edits). Reconcile it here — the one place that
  // knows the complete derived-metric set — by unioning the enum with every
  // derived_metric_key. Only touch an already enum-constrained `metrics` param:
  // a free-form (no-enum) one already accepts any metric, and we don't fabricate
  // a constraint the user never defined, nor invent the param if it's absent.
  const allDerivedKeys = derivedMetrics.map((d) => d.derived_metric_key);
  const inputSchema: Record<string, Record<string, unknown>> = {};
  const required: string[] = [];
  for (const param of config.parameters) {
    const spec = buildParamSpec(param);
    if (param.parameterName === METRICS_PARAM_NAME && Array.isArray(spec.enum) && spec.enum.length > 0) {
      const merged = [...(spec.enum as string[])];
      for (const key of allDerivedKeys) {
        if (!merged.includes(key)) merged.push(key);
      }
      spec.enum = merged;
    }
    inputSchema[param.parameterName] = spec;
    if (param.required) {
      required.push(param.parameterName);
    }
  }

  // ── Assemble in EXACT top-level key order ──────────────────────────────
  const obj: Record<string, unknown> = {
    tool_name: config.toolName,
    tool_type: config.toolType,
    category: config.category,
    description: config.toolDescription,
    database: {
      type: 'redshift',
      cluster_identifier: config.database.clusterIdentifier,
      database: config.database.database,
      secret_arn: {
        test,
        beta,
        live,
      },
    },
    stored_procedure: config.storedProcedure || DEFAULT_STORED_PROCEDURE,
    source_table_name: config.sourceTableName,
    // The SP resolves EVERY requested metric against these two tables (it never
    // reads the base_metrics/derived_metrics blocks below — those are seed
    // metadata). They must name real tables or every query raises
    // "Derived metric not found". The Review step's catalog section creates them.
    base_metrics_catalog: catalogTables.baseQualified,
    derived_metrics_catalog: catalogTables.derivedQualified,
    output_schema: OUTPUT_SCHEMA,
    base_metrics: baseMetrics,
    derived_metrics: derivedMetrics,
    metadata: {
      dashboard_link: '',
      team_contact: '',
    },
    input_schema: inputSchema,
    required,
  };

  const dumped = yaml.dump(obj, {
    lineWidth: -1,
    noRefs: true,
    quotingType: '"',
    sortKeys: false,
  });

  return { yaml: addSectionSpacing(collapseExamples(collapseEnums(dumped))), warnings };
}
